import { useState, useRef } from 'react'
import { useFetcher } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import {
	Dropzone,
	DropzoneContent,
	DropzoneEmptyState,
} from '#app/components/ui/dropzone.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '#app/components/ui/sheet.tsx'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'
import {
	useSupabaseUpload,
	type MediaItem,
} from '#app/hooks/use-supabase-upload.ts'
import { getAllowedMimeTypes } from '#app/routes/resources+/upload-media.tsx'
import { Skeleton } from '../ui/skeleton.tsx'
import { MediaCard } from './media-card.tsx'
import { MediaEditPanel } from './media-edit-panel.tsx'

interface MediaDialogProps {
	/**
	 * Form field name for hidden input (Conform integration)
	 */
	name?: string
	/**
	 * Callback for when selection changes (alternative to form integration)
	 */
	onChange?: (selectedMedia: MediaItem[]) => void
	/**
	 * Allow multiple selection
	 */
	multiple?: boolean
	/**
	 * Button text for trigger
	 */
	buttonText?: string
	/**
	 * Dialog title
	 */
	title?: string
	/**
	 * Upload path for new files
	 */
	uploadPath?: string
	/**
	 * Allowed MIME types for uploads
	 */
	allowedMimeTypes?: string[]
	/**
	 * Maximum files for upload
	 */
	maxFiles?: number
	/**
	 * Maximum file size for upload
	 */
	maxFileSize?: number
	/**
	 * Initial selected media objects (for immediate preview)
	 */
	initialSelectedMedia?: MediaItem[]
	/**
	 * Custom trigger element
	 */
	trigger?: React.ReactNode
	/**
	 * Custom className for trigger button
	 */
	className?: string
	/**
	 * Hide the selected media preview (useful for insertion-only use cases)
	 */
	hidePreview?: boolean
}

export function MediaDialog({
	name,
	onChange,
	multiple = false,
	buttonText,
	title,
	uploadPath = 'general',
	allowedMimeTypes = getAllowedMimeTypes(['IMAGES', 'DOCUMENTS']),
	maxFiles = 5,
	maxFileSize = 1000 * 1000 * 10, // 10MB
	initialSelectedMedia = [],
	trigger,
	className,
	hidePreview = false,
}: MediaDialogProps) {
	const [open, setOpen] = useState(false)
	const [activeTab, setActiveTab] = useState('select')
	const [selectedMedia, setSelectedMedia] =
		useState<MediaItem[]>(initialSelectedMedia)
	const [editingMedia, setEditingMedia] = useState<MediaItem | null>(null)
	const [editPanelOpen, setEditPanelOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')

	// Single fetcher for all media operations
	const mediaFetcher = useFetcher<{
		items?: MediaItem[]
		pagination?: {
			page: number
			totalCount: number
			hasNextPage: boolean
		}
	}>()

	// Dropzone setup - let it handle uploads internally
	const dropzoneProps = useSupabaseUpload({
		bucketName: 'public-assets',
		path: uploadPath,
		allowedMimeTypes,
		maxFiles,
		maxFileSize,
	})

	// Load media with search
	const loadMedia = (search = '') => {
		const params = new URLSearchParams()
		if (search) params.set('q', search)
		void mediaFetcher.load(`/resources/media?${params}`)
	}

	// Handle dialog open - just load media
	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen)
		if (newOpen) {
			loadMedia()
		}
	}

	// Handle search
	const handleSearch = (query: string) => {
		setSearchQuery(query)
		loadMedia(query)
	}

	// Handle dropzone upload success - refresh media list
	const dropzoneWasLoading = useRef(false)
	if (dropzoneProps.loading) {
		dropzoneWasLoading.current = true
	}
	if (
		dropzoneWasLoading.current &&
		!dropzoneProps.loading &&
		dropzoneProps.isSuccess
	) {
		dropzoneWasLoading.current = false
		// Refresh media list to show newly uploaded items
		queueMicrotask(() => {
			loadMedia(searchQuery)
			setActiveTab('select') // Switch to browse tab to see the uploaded media
		})
	}

	// Handle media selection
	const handleMediaSelect = (media: MediaItem) => {
		if (multiple) {
			setSelectedMedia((prev) => {
				const isSelected = prev.some((m) => m.id === media.id)
				if (isSelected) {
					return prev.filter((m) => m.id !== media.id)
				} else {
					return [...prev, media]
				}
			})
		} else {
			const newSelection = [media]
			setSelectedMedia(newSelection)
			if (onChange) {
				onChange(newSelection)
			}
			setOpen(false)
		}
	}

	// Handle done button
	const handleDone = () => {
		if (onChange) {
			onChange(derivedSelectedMedia)
		}
		setOpen(false)
	}

	// Initial selection is handled via initialSelectedMedia prop

	// Get current media items - resources route always returns items
	const mediaItems = mediaFetcher.data?.items || []

	// Keep selection simple - no derived logic
	const derivedSelectedMedia = selectedMedia

	// Keep tab switching simple - user controls it
	const currentTab = activeTab

	// Generate display text
	const defaultButtonText = multiple ? 'Select Images' : 'Select Image'
	const displayButtonText =
		buttonText ||
		(derivedSelectedMedia.length > 0 && !hidePreview
			? `Change ${multiple ? 'Images' : 'Image'} (${derivedSelectedMedia.length} selected)`
			: buttonText || defaultButtonText)
	const displayTitle = title || (multiple ? 'Select Images' : 'Select Image')

	return (
		<>
			{/* Hidden inputs for form integration */}
			{name && (
				<>
					{multiple ? (
						derivedSelectedMedia.map((media) => (
							<input
								key={media.id}
								type="hidden"
								name={name}
								value={media.id}
							/>
						))
					) : (
						<input
							type="hidden"
							name={name}
							value={derivedSelectedMedia[0]?.id || ''}
						/>
					)}
				</>
			)}

			{/* Selected media preview */}
			{derivedSelectedMedia.length > 0 && !hidePreview && (
				<div className="mb-4 space-y-2">
					<div className="flex flex-wrap gap-2 border-none">
						{derivedSelectedMedia.map((media) => (
							<div key={media.id} className="relative">
								<MediaCard
									media={media}
									showSelectionIndicator={false}
									onEdit={() => {
										setEditingMedia(media)
										setEditPanelOpen(true)
									}}
									className="cursor-pointer"
								/>
								<Button
									type="button"
									variant="destructive"
									size="sm"
									className="absolute -top-3 -right-3 size-6 rounded-full p-0"
									onClick={() => {
										setSelectedMedia((prev) =>
											prev.filter((m) => m.id !== media.id),
										)
									}}
								>
									<Icon name="cross-1" className="size-3" />
								</Button>
							</div>
						))}
					</div>
				</div>
			)}

			<Sheet open={open} onOpenChange={handleOpenChange}>
				<SheetTrigger asChild>
					{trigger || (
						<Button type="button" variant="outline" className={className}>
							<Icon name="image" />
							{displayButtonText}
						</Button>
					)}
				</SheetTrigger>

				<SheetContent>
					<SheetHeader>
						<SheetTitle>{displayTitle}</SheetTitle>
						<SheetDescription>
							Select or upload media files to use.
						</SheetDescription>
					</SheetHeader>

					<div className="mt-2">
						<Tabs value={currentTab} onValueChange={setActiveTab}>
							<TabsList>
								<TabsTrigger value="select">Browse</TabsTrigger>
								<TabsTrigger value="upload">Upload</TabsTrigger>
							</TabsList>

							<TabsContent value="select">
								{/* Search */}
								<div className="mb-4">
									<Input
										placeholder="Search media..."
										value={searchQuery}
										onChange={(e) => handleSearch(e.target.value)}
										className="w-full"
									/>
								</div>

								{/* Media Grid */}
								{mediaFetcher.state === 'loading' ? (
									<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
										{Array.from({ length: 12 }).map((_, index) => (
											<Skeleton key={index} className="h-40 w-full" />
										))}
									</div>
								) : mediaItems.length > 0 ? (
									<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
										{mediaItems.map((media) => (
											<MediaCard
												key={media.id}
												media={media}
												isSelected={derivedSelectedMedia.some(
													(m) => m.id === media.id,
												)}
												onClick={() => handleMediaSelect(media)}
												onEdit={() => {
													setEditingMedia(media)
													setEditPanelOpen(true)
												}}
												showSelectionIndicator={true}
												className="cursor-pointer"
											/>
										))}
									</div>
								) : (
									<div className="flex flex-col items-center justify-center py-12">
										<Icon name="image" className="size-12 opacity-50" />
										<h3 className="mt-4 font-medium">No media found</h3>
										<p className="text-muted-foreground text-sm">
											Try adjusting your search or upload some files.
										</p>
									</div>
								)}
							</TabsContent>

							<TabsContent value="upload">
								<div className="space-y-4">
									<Dropzone {...dropzoneProps} className="min-h-[200px]">
										<DropzoneEmptyState />
										<DropzoneContent />
									</Dropzone>
								</div>
							</TabsContent>
						</Tabs>

						{/* Done Button (for multiple selection) */}
						{multiple && derivedSelectedMedia.length > 0 && (
							<div className="mt-6 flex justify-end">
								<Button onClick={handleDone}>
									Done ({derivedSelectedMedia.length} selected)
								</Button>
							</div>
						)}
					</div>
				</SheetContent>
			</Sheet>

			{/* Edit Panel */}
			{editingMedia && (
				<MediaEditPanel
					media={editingMedia}
					open={editPanelOpen}
					onOpenChange={(open) => {
						setEditPanelOpen(open)
						if (!open) {
							// Delay clearing editingMedia to allow animation to complete
							setTimeout(() => setEditingMedia(null), 300)
						}
					}}
					onUpdate={(updatedMedia) => {
						// Update selected media if it was edited
						setSelectedMedia((prev) =>
							prev.map((item) =>
								item.id === updatedMedia.id ? updatedMedia : item,
							),
						)
						// Refresh the media list to show updated data
						void loadMedia()
						setEditPanelOpen(false)
						setTimeout(() => setEditingMedia(null), 300)
					}}
				/>
			)}
		</>
	)
}
